import './AuthLayout.css';

import { Loader2 } from 'lucide-react';
import { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import DefaultCompanyLogo from '@/assets/icons/default-company-logo.svg?react';
import AuthBackground from '@/assets/images/auth-background.png';
import WelcomeBack from '@/assets/images/welcome-back.png';
import { useGetZorGeneralSetting } from '@/hooks/zor-general-setting/useGetZorGeneralSetting';
import { useZorGeneralSettingStore } from '@/stores/useZorGeneralSettingStore';

interface AuthLayoutProps {
  children: ReactNode;
}

export const AuthLayout = ({ children }: AuthLayoutProps) => {
  const location = useLocation();

  // custom hooks
  const { result, isLoading, isError } = useGetZorGeneralSetting();

  // stores
  const { zorGeneralSetting, setZorGeneralSetting } =
    useZorGeneralSettingStore();

  useEffect(() => {
    if (result?.data) {
      setZorGeneralSetting(result.data);
    }
  }, [result, setZorGeneralSetting]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="size-4 animate-spin" />
      </div>
    );
  }

  if (
    location.pathname.includes('confirm') ||
    location.pathname.includes('join-org') ||
    location.pathname.includes('sso-callback') ||
    location.pathname.includes('verify-token')
  ) {
    return children;
  }

  // derived values
  const companyLogoUrl = zorGeneralSetting?.company_logo_url;
  const headingText =
    zorGeneralSetting?.login_greeting_text_h1 ||
    'Data-Driven Franchise Activation & AI Automations';
  const descriptionText =
    zorGeneralSetting?.login_greeting_text_h2 ||
    'Discover ideal franchisee partners in comparable markets with prime location potential.';

  return (
    <div className="flex h-screen w-screen">
      {children}
      <div className="auth-bg relative flex h-full w-full flex-col items-center justify-center gap-24 px-12 max-lg:hidden lg:w-1/2 xl:px-24">
        <img
          src={AuthBackground}
          loading="lazy"
          alt="Authentication background"
          className="absolute left-0 top-0 z-[-1] h-full w-full object-cover object-center"
        />
        <div className="image-wrapper rounded-2xl p-3">
          {isLoading ? (
            <div className="flex h-[400px] w-[400px] items-center justify-center">
              <Loader2 className="size-4 animate-spin" />
            </div>
          ) : companyLogoUrl && !isError ? (
            <img
              src={companyLogoUrl}
              loading="lazy"
              alt="Company logo"
              className="h-auto max-h-[400px] w-auto"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.nextElementSibling?.classList.remove('hidden');
              }}
            />
          ) : null}
          {/* Fallback logo */}
          <div
            className={`flex h-[400px] w-[400px] items-center justify-center ${companyLogoUrl && !isError ? 'hidden' : ''}`}
          >
            {isError ? (
              <DefaultCompanyLogo className="h-32 w-32 text-white/50" />
            ) : (
              <img
                src={WelcomeBack}
                loading="lazy"
                alt="Welcome back illustration"
                className="h-auto max-h-[400px] w-auto"
              />
            )}
          </div>
        </div>
        <div className="flex flex-col items-center justify-center gap-5">
          <h1 className="as-strong gradient-text max-w-[560px] text-center">
            {headingText}
          </h1>
          <p className="as-body-01 max-w-[520px] text-center text-white">
            {descriptionText}
          </p>
        </div>
      </div>
    </div>
  );
};
